/**
 * Schritt 0: Info und FAQ
 * Informationsseite mit Beschreibung der Anwendung und häufig gestellten Fragen
 */

import React from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  ExpandableSection,
  Alert,
  ColumnLayout
} from '@cloudscape-design/components';

/**
 * Komponente für Informationen und FAQ
 */
const Schritt0: React.FC = () => {
  // FAQ-Daten
  const faqItems = [
    {
      id: 'was-ist-portfolio-wizard',
      question: 'Was ist der Portfolio Wizard?',
      answer: 'Eine Webanwendung zur Darstellung und Simulation steuerlicher Effekte bei Wertpapierverkäufen. Ziel ist es, Ihnen einen Überblick zu geben, wie sich unterschiedliche Verkaufs-Szenarien auf die Steuerlast auswirken könnten. Die Anwendung ersetzt keine steuerliche Beratung.'
    },
    {
      id: 'welche-daten-benoetigt',
      question: 'Welche Daten benötige ich?',
      answer: 'Transaktionsdaten über die Parqet API (ISIN, Datum, Typ, Name, Preis, Anzahl, Gesamtwert, Währung, Gebühren, Steuer).'
    },
    {
      id: 'steuerberechnung',
      question: 'Wie funktioniert die Steuerberechnung?',
      answer: 'Nach dem gesetzlich vorgesehenen FIFO-Verfahren (First-In-First-Out), vereinfacht und standardisiert dargestellt, mit standardisierter Anwendung von Teilfreistellungen und Freibeträgen gemäß Ihren Eingaben.'
    },
    {
      id: 'verkaufsszenarien',
      question: 'Welche Verkaufs-Szenarien kann ich simulieren?',
      answer: 'Gleichmäßige Verteilung (symmetrisch), benutzerdefinierte Auswahl oder rein mathematisch priorisierte Reihenfolgen – ohne individuelle Handlungsempfehlung.'
    },
    {
      id: 'guenstigerpruefung',
      question: 'Was ist der Modellvergleich in diesem Tool?',
      answer: 'Eine rein mathematische Beispielrechnung, die zeigt, wie sich Abgeltungsteuer und ein angenäherter Durchschnittssteuersatz unterscheiden könnten. Diese Berechnung basiert auf pauschalen Annahmen und ist nicht auf Ihre individuelle Situation zugeschnitten.'
    }
  ];

  // Feature-Karten
  const features = [
    {
      icon: 'calculator',
      title: 'Steuerbeispielrechnung',
      description: 'FIFO-Prinzip unter Berücksichtigung von Teilfreistellungen und Freibeträgen - nur in vereinfachter Form, nicht für Ihre persönliche Steuererklärung geeignet'
    },
    {
      icon: 'analytics',
      title: 'Portfolio-Analyse',
      description: 'Portfolio-Analyse mit detaillierter Übersicht über Positionen und Performance-Kennzahlen'
    },
    {
      icon: 'settings',
      title: 'Szenarien-Vergleich',
      description: 'Szenarien-Vergleich verschiedener Verkaufsreihenfolgen (z. B. gleichmäßige Verteilung oder benutzerdefinierte Auswahl) – ohne individuelle Handlungsempfehlung'
    },
    {
      icon: 'file',
      title: 'Parqet-Import',
      description: 'Es wird die neue Connect API von Parqet genutzt mit direktem Lese-Zugriff auf die Portfolios. Darüber werden neben den Transaktionsdaten, die für die Steuerberechnung notwendig sind, die aktuellen Asset-Kurse von Parqet bereitgestellt.'
    }
  ];

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Willkommen-Sektion */}
      <Container
        header={
          <Header
            variant="h2"
            description="Darstellung und Beispielrechnung möglicher steuerlicher Effekte bei Wertpapierverkäufen (Depotentnahmen) auf Basis vereinfachter Modellannahmen"
          >
            Willkommen beim Portfolio Wizard für Parqet
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <Alert
            type="warning"
            dismissAriaLabel="Schließen"
            header="Wichtiger rechtlicher Hinweis"
          >
            Dieses Tool dient ausschließlich zur allgemeinen, unverbindlichen Information. Es berücksichtigt nicht alle individuellen steuerlichen Besonderheiten und ersetzt keine steuerliche Beratung durch eine Steuerberaterin oder einen Steuerberater. Es erfolgt keine Prüfung oder individuelle Bewertung Ihrer Angaben. Für verbindliche Auskünfte oder Entscheidungen zu Ihren Steuern wenden Sie sich bitte an eine Steuerberaterin oder einen Steuerberater. Die Ergebnisse dieser Anwendung können und dürfen nicht als Entscheidungsgrundlage für Ihre persönliche Steuererklärung genutzt werden.
          </Alert>

          <Alert
            type="info"
            dismissAriaLabel="Schließen"
            header="100% Datenschutz"
          >
            <Box variant="p">• Keine Datenübertragung an externe Server • Berechnungen ausschließlich auf Ihrem Endgerät</Box>
          </Alert>

          {/* Feature-Übersicht */}
          <ColumnLayout columns={2}>
            {features.map((feature, index) => (
              <div key={index} style={{ 
                padding: '16px', 
                border: '1px solid #d5dbdb', 
                borderRadius: '8px',
                backgroundColor: '#fafbfc'
              }}>
                <Box variant="h4" margin={{ bottom: 's' }}>
                  {feature.title}
                </Box>
                <Box variant="p" color="text-body-secondary">
                  {feature.description}
                </Box>
              </div>
            ))}
          </ColumnLayout>
        </SpaceBetween>
      </Container>

      {/* Funktionsweise */}
      <Container
        header={
          <Header variant="h3">
            So funktioniert der Portfolio Wizard
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="s">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              minWidth: '32px', 
              height: '32px', 
              borderRadius: '50%', 
              backgroundColor: '#0972d3', 
              color: 'white', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              fontSize: '14px', 
              fontWeight: 'bold' 
            }}>
              1
            </div>
            <Box variant="p">
              <strong>Berechnungsparameter eingeben:</strong> Steuerjahr, Familienstand, Kirchensteuersatz
            </Box>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              minWidth: '32px', 
              height: '32px', 
              borderRadius: '50%', 
              backgroundColor: '#0972d3', 
              color: 'white', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              fontSize: '14px', 
              fontWeight: 'bold' 
            }}>
              2
            </div>
            <Box variant="p">
              <strong>Transaktionsdaten importieren:</strong> Daten über die Parqet API laden
            </Box>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              minWidth: '32px', 
              height: '32px', 
              borderRadius: '50%', 
              backgroundColor: '#0972d3', 
              color: 'white', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              fontSize: '14px', 
              fontWeight: 'bold' 
            }}>
              3
            </div>
            <Box variant="p">
              <strong>Portfolio analysieren:</strong> Auswirkungen verschiedener Verkaufs-Szenarien aufzeigen lassen
            </Box>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              minWidth: '32px', 
              height: '32px', 
              borderRadius: '50%', 
              backgroundColor: '#0972d3', 
              color: 'white', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              fontSize: '14px', 
              fontWeight: 'bold' 
            }}>
              4
            </div>
            <Box variant="p">
              <strong>Steuervergleich durchführen:</strong> Optional prüfen, wie sich pauschale Kapitalertragsteuer und ein angenäherter Durchschnittssteuersatz unterscheiden (nur zu Demonstrationszwecken)
            </Box>
          </div>
        </SpaceBetween>
      </Container>

      {/* FAQ-Sektion */}
      <Container
        header={
          <Header variant="h3">
            Häufig gestellte Fragen (FAQ)
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="s">
          {faqItems.map((item) => (
            <ExpandableSection
              key={item.id}
              headerText={item.question}
              variant="container"
              defaultExpanded={true}
            >
              <Box variant="p">
                {item.answer}
              </Box>
            </ExpandableSection>
          ))}
        </SpaceBetween>
      </Container>



      {/* Erste Schritte */}
      <Container
        header={
          <Header variant="h3">
            Bereit zum Start?
          </Header>
        }
      >
        <Box variant="p">
          Beginnen Sie mit <strong>Schritt 1: Berechnungsparameter</strong>, um Ihre Berechnungsparameter zu konfigurieren. 
          Anschließend können Sie in Schritt 2 Ihre Transaktionsdaten importieren und mit der Portfolio-Analyse starten.
        </Box>
      </Container>
    </SpaceBetween>
  );
};

export default Schritt0;